import { useRef, useCallback, useState, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';

interface DetectionResult {
  class: string;
  score: number;
  bbox: [number, number, number, number];
}

interface UseObjectDetectionProps {
  targetObjects: string[];
  confidenceThreshold?: number;
  onDetection?: (detections: DetectionResult[]) => void;
}

export function useObjectDetection({ 
  targetObjects, 
  confidenceThreshold = 0.6,
  onDetection 
}: UseObjectDetectionProps) {
  const [model, setModel] = useState<cocoSsd.ObjectDetection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetecting, setIsDetecting] = useState(false);
  const detectionIntervalRef = useRef<NodeJS.Timeout>();

  // Object class mappings for COCO-SSD model
  const objectMappings: { [key: string]: string[] } = {
    'bottle': ['bottle'],
    'plastic_bottle': ['bottle'],
    'water_bottle': ['bottle'],
    'glass_bottle': ['bottle', 'wine glass'],
    'bag': ['handbag', 'backpack', 'suitcase'],
    'shopping_bag': ['handbag', 'backpack'],
    'reusable_bag': ['handbag', 'backpack'],
    'paper_bag': ['handbag'],
    'vegetables': ['broccoli', 'carrot', 'apple', 'banana', 'orange'],
    'fruit': ['apple', 'banana', 'orange'],
    'salad': ['broccoli', 'apple', 'banana'],
    'plant_food': ['broccoli', 'carrot', 'apple', 'banana', 'orange'],
    'bicycle': ['bicycle'],
    'bike': ['bicycle'],
    'led_bulb': ['light bulb'],
    'light_bulb': ['light bulb'],
    'bulb': ['light bulb'],
    'compost': ['apple', 'banana', 'orange'],
    'compost_bin': ['bowl', 'cup'],
    'organic_waste': ['apple', 'banana', 'orange'],
    'reusable_bottle': ['bottle', 'cup'],
    'produce': ['apple', 'banana', 'orange', 'broccoli', 'carrot'],
    'fresh_food': ['apple', 'banana', 'orange', 'broccoli', 'carrot']
  };

  // Initialize COCO-SSD model only
  useEffect(() => {
    const initializeModel = async () => {
      try {
        setIsLoading(true);
        
        // Set TensorFlow.js backend
        await tf.ready();
        console.log('TensorFlow.js backend:', tf.getBackend());
        
        // Load COCO-SSD model for object detection
        console.log('Loading COCO-SSD model...');
        const loadedModel = await cocoSsd.load();
        setModel(loadedModel);
        console.log('COCO-SSD model loaded successfully');
      } catch (error) {
        console.error('Error loading object detection model:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeModel();
  }, []);

  // Check if detected class matches any target objects
  const isTargetObject = useCallback((detectedClass: string): boolean => {
    return targetObjects.some(target => {
      const mappedClasses = objectMappings[target.toLowerCase()] || [target.toLowerCase()];
      return mappedClasses.some(mappedClass => 
        detectedClass.toLowerCase().includes(mappedClass.toLowerCase()) ||
        mappedClass.toLowerCase().includes(detectedClass.toLowerCase())
      );
    });
  }, [targetObjects]);

  // Perform object detection using COCO-SSD model
  const detectObjects = useCallback(async (videoElement: HTMLVideoElement): Promise<DetectionResult[]> => {
    if (!model || !videoElement || videoElement.videoWidth === 0) {
      return [];
    }

    try {
      console.log('Running COCO-SSD detection...');
      const predictions = await model.detect(videoElement);
      
      const validDetections = predictions
        .filter(prediction => 
          prediction.score >= confidenceThreshold && 
          isTargetObject(prediction.class)
        )
        .map(prediction => ({
          class: prediction.class,
          score: prediction.score,
          bbox: prediction.bbox
        }));

      console.log('COCO-SSD detections:', validDetections);

      if (onDetection && validDetections.length > 0) {
        onDetection(validDetections);
      }

      return validDetections;
    } catch (error) {
      console.error('Error during object detection:', error);
      return [];
    }
  }, [model, confidenceThreshold, isTargetObject, onDetection]);

  // Start continuous detection
  const startDetection = useCallback((videoElement: HTMLVideoElement, intervalMs: number = 500) => {
    if (!model || isDetecting) {
      console.log('Cannot start detection:', { hasModel: !!model, isDetecting });
      return;
    }

    console.log('Starting object detection...');
    setIsDetecting(true);
    detectionIntervalRef.current = setInterval(() => {
      detectObjects(videoElement);
    }, intervalMs);
  }, [model, isDetecting, detectObjects]);

  // Stop detection
  const stopDetection = useCallback(() => {
    console.log('Stopping object detection...');
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = undefined;
    }
    setIsDetecting(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDetection();
    };
  }, [stopDetection]);

  return {
    model,
    isLoading,
    isDetecting,
    detectObjects,
    startDetection,
    stopDetection,
    isTargetObject
  };
}